import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import FixaCheckbox from "@coworker/apprestructured/src/shared/wrappers/FixaCheckbox/FixaCheckbox";
import FixaLoadingBall from "@coworker/apprestructured/src/shared/wrappers/FixaLoading/FixaLoadingBall";
import FixaPill from "@coworker/apprestructured/src/shared/wrappers/FixaPill/FixaPill";
import {
  CountryGrid,
  CountryHeader,
  CountryLabel,
  CountryStoresBlock,
  CountryStoresHeader,
  PillRow,
  SectionTitle,
  SelectedRow,
  StoresPanel,
} from "./AtkomstUsersPage.styles";
import { formatNodeLabel } from "../utils/atkomstUsersUtils";

interface CountryNode {
  ref: string;
  name: string;
}

interface StoreNode {
  ref: string;
  name: string;
  countryId: string;
}

interface Props {
  countryNodes: CountryNode[];
  storesByCountry: Map<string, StoreNode[]>;
  nodeNameByRef: Map<string, string>;
  selectedNodeRefs: string[];
  selectedCountryIds: string[];
  isLoadingCountries: boolean;
  isLoadingStores: boolean;
  onToggleNode: (ref: string) => void;
  onToggleAllStoresInCountry: (countryId: string, select: boolean) => void;
  onToggleAllCountries: () => void;
  onClearSelection: () => void;
}

export const CountryStorePicker = ({
  countryNodes,
  storesByCountry,
  nodeNameByRef,
  selectedNodeRefs,
  selectedCountryIds,
  isLoadingCountries,
  isLoadingStores,
  onToggleNode,
  onToggleAllStoresInCountry,
  onToggleAllCountries,
  onClearSelection,
}: Props) => {
  const allCountriesSelected =
    countryNodes.length > 0 &&
    countryNodes.every((c) => selectedNodeRefs.includes(c.ref));

  return (
    <>
      <div>
        <CountryHeader>
          <SectionTitle>Countries</SectionTitle>
          {countryNodes.length > 0 && (
            <FixaButton
              type="tertiary"
              size="xsmall"
              onClick={onToggleAllCountries}
            >
              {allCountriesSelected ? "Deselect all" : "Select all"}
            </FixaButton>
          )}
        </CountryHeader>
        {isLoadingCountries ? (
          <FixaLoadingBall size="small" />
        ) : (
          <CountryGrid>
            {countryNodes.map((node) => (
              <FixaCheckbox
                key={node.ref}
                id={`country-${node.ref}`}
                value={node.ref}
                label={node.name}
                checked={selectedNodeRefs.includes(node.ref)}
                onChange={() => onToggleNode(node.ref)}
              />
            ))}
            {!countryNodes.length && <span>No countries available.</span>}
          </CountryGrid>
        )}
      </div>

      {selectedCountryIds.length > 0 && (
        <div>
          <SectionTitle>
            Stores in selected countries ({selectedCountryIds.length})
          </SectionTitle>
          <StoresPanel>
            {selectedCountryIds.map((countryId) => {
              const countryStores = storesByCountry.get(countryId) ?? [];
              const allSelected =
                countryStores.length > 0 &&
                countryStores.every((s) => selectedNodeRefs.includes(s.ref));
              return (
                <CountryStoresBlock key={countryId}>
                  <CountryStoresHeader>
                    <CountryLabel>
                      {countryId} — {countryStores.length} store
                      {countryStores.length === 1 ? "" : "s"}
                    </CountryLabel>
                    {countryStores.length > 0 && (
                      <FixaButton
                        type="tertiary"
                        size="xsmall"
                        onClick={() =>
                          onToggleAllStoresInCountry(countryId, !allSelected)
                        }
                      >
                        {allSelected
                          ? "Deselect all stores"
                          : "Select all stores"}
                      </FixaButton>
                    )}
                  </CountryStoresHeader>
                  {countryStores.length === 0 ? (
                    <span>
                      {isLoadingStores
                        ? "Loading stores…"
                        : `No stores found for ${countryId}.`}
                    </span>
                  ) : (
                    <PillRow>
                      {countryStores.map((node) => (
                        <FixaPill
                          key={node.ref}
                          label={formatNodeLabel(node.ref, node.name)}
                          selected={selectedNodeRefs.includes(node.ref)}
                          onClick={() => onToggleNode(node.ref)}
                          size="small"
                        />
                      ))}
                    </PillRow>
                  )}
                </CountryStoresBlock>
              );
            })}
          </StoresPanel>
        </div>
      )}

      {selectedNodeRefs.length > 0 && (
        <div>
          <SectionTitle>Selected ({selectedNodeRefs.length})</SectionTitle>
          <SelectedRow>
            {selectedNodeRefs.map((ref) => (
              <FixaPill
                key={ref}
                label={formatNodeLabel(ref, nodeNameByRef.get(ref))}
                selected
                onClick={() => onToggleNode(ref)}
                size="small"
              />
            ))}
            <FixaButton type="tertiary" size="small" onClick={onClearSelection}>
              Clear all
            </FixaButton>
          </SelectedRow>
        </div>
      )}
    </>
  );
};
